import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  Button,
} from 'grommet';
import { SimpleModal } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { StyledServiceOptionContainer, StyledUppercaseText, StyledStrikethroughText } from '../StyledOnboardingComponents';
import {
  getCidPricing,
  getCidLabel,
  getUploaderLabel,
  getSocialMonthly,
  getEarlyBirdCidSetupFee,
} from '../pricingHelpers';

const ServiceSummaryContainer = ({
  title,
  children,
  borderColor = null,
  onChange = null,
  onRemove = null,
  badge = null,
  ...rest
}) => {
  const { theme: { page, button } } = useAppTheme();
  return (
    <StyledServiceOptionContainer
      noHover
      style={{
        border: borderColor ? `1px solid ${borderColor}` : 'none',
      }}
      pad="1rem"
      round="var(--app-radius)"
      width="100%"
      gap="0.5rem"
      active={false}
      {...rest}
    >
      <Box direction="row" align="center" justify="between" width="100%">
        <Box direction="row" align="center" gap="0.5rem">
          <Text size="1.1rem" weight={700} color={page.primaryText}>{title}</Text>
          {badge}
        </Box>
        <Box direction="row" gap="0.5rem">
          {onChange && (
            <Button
              plain
              color={button.primaryBg}
              fontWeight={600}
              label="Change"
              style={{
                textDecoration: 'underline',
                fontSize: '0.85rem',
                minWidth: 0,
                padding: 0,
              }}
              onClick={onChange}
            />
          )}
          {onRemove && (
            <Button
              plain
              color={button.primaryBg}
              fontWeight={600}
              label="Remove"
              style={{
                textDecoration: 'underline',
                fontSize: '0.85rem',
                minWidth: 0,
                padding: 0,
              }}
              onClick={onRemove}
            />
          )}
        </Box>
      </Box>
      {children}
    </StyledServiceOptionContainer>
  );
};

ServiceSummaryContainer.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  badge: PropTypes.node,
  borderColor: PropTypes.string,
};

const PricingStep = ({
  small,
  offerings,
  accountKind,
  channels = [],
  uploader = 'redflag',
  cidPlan = 'A',
  socialPlatforms = [],
  socialMainLink = '',
  goToStep,
  setOfferings,
  socialPlatformLabels = {},
  pricingData = null,
  earlyBirdEligibility = null,
}) => {
  const { theme: { page, button } } = useAppTheme();

  // --- Dynamic Pricing Logic and Helpers ---
  const cidPricing = getCidPricing(pricingData, uploader, cidPlan);
  const cidSetupFee = cidPricing?.setupFeeAmount ?? 0;
  const cidMonthlyFee = cidPricing?.monthlyFeeAmount ?? 0;
  const channelCount = channels.length;
  const channelMultiplier = offerings.useCid ? channelCount : 1;
  const socialMonthly = getSocialMonthly(
    pricingData, offerings, socialPlatforms, channelMultiplier,
  );
  const earlyBirdSetupFee = getEarlyBirdCidSetupFee(pricingData, uploader, cidPlan);
  const isEarlyBirdEligible = Boolean(earlyBirdEligibility?.eligible);
  const withEarlyBirdPricing = isEarlyBirdEligible && earlyBirdSetupFee != null;

  // Use early bird fee per channel if eligible, otherwise normal fee
  const perChannelSetupFee = withEarlyBirdPricing
    ? earlyBirdSetupFee
    : cidSetupFee;
  const totalCidSetupFee = offerings.useCid ? perChannelSetupFee * channelCount : 0;

  const estimatedTotal = (offerings.useCid ? cidMonthlyFee * channelCount : 0) + socialMonthly;
  const useSocialMainLink = offerings.useCid && channels?.length > 0
    ? channels?.[0]
    : socialMainLink;

  const [showChannelsModal, setShowChannelsModal] = React.useState(false);
  const [showSocialModal, setShowSocialModal] = React.useState(false);

  const handleRemoveFeature = (feature) => {
    setOfferings({ ...offerings, [feature]: false });
  };

  // --- Layout helpers ---
  const summaryListItem = (label, selected) => (
    <Box direction="row" justify="start" align="center" gap="0.25rem">
      <Text size="1rem" style={{ wordBreak: 'keep-all' }} color={page.secondaryText}>
        {label}
        :
      </Text>
      <Text size="1rem" color={button.primaryBg} weight={600}>
        {selected ? 'Selected' : 'Not selected'}
      </Text>
    </Box>
  );

  const summaryBoxWidth = offerings.useCid ? '23%' : '32%';

  const renderSummaryBox = (
    title, amount, highlight = false, strikethrough = null, badge = null,
  ) => (
    <Box
      width={small ? '100%' : summaryBoxWidth}
      flex="grow"
      background={highlight ? button.selectBg : page.cardBg}
      round="var(--app-radius)"
      pad="1rem"
      align="start"
      justify="start"
      gap="0.5rem"
    >
      <Box direction="row" align="center" gap="0.5rem">
        <Text size="1rem" weight={700} color={highlight ? page.altHighlight : page.secondaryText}>{title}</Text>
        {badge}
      </Box>
      <Text size="1.35rem" weight={700} color={page.primaryText}>
        {strikethrough && (
          <StyledStrikethroughText as="span">
            $
            {strikethrough}
            .00
          </StyledStrikethroughText>
        )}
        {' '}
        $
        {amount}
        .00
      </Text>
    </Box>
  );

  // --- Setup Fee Strikethrough Helper ---
  const setupFeeStrikethrough = () => {
    if (isEarlyBirdEligible && earlyBirdSetupFee != null) {
      return cidSetupFee * channelCount;
    }
    return null;
  };

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>
          Plan Summary
        </Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Confirm your choices and get a plan cost estimate.
        </Text>
      </Box>
      {/* Pricing Summary Row */}
      <Box
        direction={small ? 'column' : 'row'}
        gap="0.5rem"
        width="100%"
        justify="between"
      >
        {offerings.useCid && (
          renderSummaryBox(
            'Setup fee',
            totalCidSetupFee,
            false,
            setupFeeStrikethrough(),
            isEarlyBirdEligible ? (
              <Box
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                round="var(--app-radius)"
                background="#FFF3CD"
              >
                <Text size="0.75rem" weight={600} color="#856404">Early Bird</Text>
              </Box>
            ) : null,
          )
        )}
        {renderSummaryBox('Content ID monthly', cidMonthlyFee)}
        {renderSummaryBox('Social Scanning monthly', socialMonthly)}
        {renderSummaryBox('Estimated total per month', estimatedTotal, true)}
      </Box>
      {/* Dynamic Summary Boxes */}
      <Box gap="1rem" width="100%" background={page.cardBg} pad="1rem" round="0.35rem">
        <ServiceSummaryContainer
          title="Services"
          onChange={() => goToStep('offerings')}
        >
          {summaryListItem('Content ID', offerings.useCid)}
          {summaryListItem('Social Scanning', offerings.useSocial)}
          {summaryListItem('Watermarking', offerings.useWatermarking)}
        </ServiceSummaryContainer>
        {offerings.useCid && (
          <ServiceSummaryContainer
            title="Content ID"
            onChange={() => goToStep('cid')}
            onRemove={() => handleRemoveFeature('useCid')}
            badge={isEarlyBirdEligible ? (
              <Box
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                round="var(--app-radius)"
                background="#FFF3CD"
              >
                <Text size="0.75rem" weight={600} color="#856404">Early Bird</Text>
              </Box>
            ) : null}
          >
            <Box gap="0.5rem" width="100%">
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Account type:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">{accountKind === 'agency' ? 'Agency' : 'Individual'}</Text>
              </Box>
              <Box direction="row" gap="0.25rem" align="center">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Channels:</Text>
                {channels.length > 0 ? (
                  <Button
                    plain
                    onClick={() => setShowChannelsModal(true)}
                    label={(
                      <Text
                        color={button.primaryBg}
                        weight={600}
                        size="1rem"
                        style={{ textDecoration: 'underline' }}
                      >
                        View (
                        {channels.length}
                        )
                      </Text>
                    )}
                  />
                ) : (
                  <Button
                    plain
                    onClick={() => goToStep('cid')}
                    label={(
                      <Text
                        color={button.primaryBg}
                        weight={600}
                        size="1rem"
                        style={{ textDecoration: 'underline' }}
                      >
                        Add channels
                      </Text>
                    )}
                  />
                )}
              </Box>
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Asset uploader:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  {getUploaderLabel(pricingData, uploader)}
                </Text>
              </Box>
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Pricing plan:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  {getCidLabel(pricingData, cidPlan)}
                </Text>
              </Box>
              <Box direction="row" gap="0.25rem" align="center">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Setup fee:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  {isEarlyBirdEligible && earlyBirdSetupFee != null ? (
                    <>
                      <StyledStrikethroughText as="span">
                        $
                        {cidSetupFee * channelCount}
                        .00
                      </StyledStrikethroughText>
                      {' '}
                      $
                      {earlyBirdSetupFee * channelCount}
                      .00
                      {channels.length > 0 && (
                        ` (${channelCount} channel${channelCount > 1 ? 's' : ''})`
                      )}
                      {' '}
                      <Text as="span" size="0.75rem" color={button.primaryBg} weight={600}>Early Bird</Text>
                    </>
                  ) : (
                    <Text as="span" size="1rem" color={page.primaryText} weight={600}>
                      $
                      {cidSetupFee * channelCount}
                      .00
                      {channels.length > 0 && (
                        ` (${channelCount} channel${channelCount > 1 ? 's' : ''})`
                      )}
                    </Text>
                  )}
                </Text>
              </Box>
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Monthly fee:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  $
                  {cidMonthlyFee * channelCount}
                  .00
                </Text>
              </Box>
            </Box>
          </ServiceSummaryContainer>
        )}
        {offerings.useSocial && (
          <ServiceSummaryContainer
            title="Social Scanning"
            onChange={() => goToStep('social')}
            onRemove={() => handleRemoveFeature('useSocial')}
          >
            <Box gap="0.5rem" width="100%">
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Platforms:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  {socialPlatforms && socialPlatforms.length > 0
                    ? socialPlatforms.map((id) => socialPlatformLabels?.[id] || id).join(', ')
                    : 'None selected'}
                </Text>
              </Box>
              <Box direction="row" gap="0.25rem" align="center">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Profile link:</Text>
                {useSocialMainLink ? (
                  <Button
                    plain
                    onClick={() => setShowSocialModal(true)}
                    label={(
                      <Text
                        color={button.primaryBg}
                        weight={600}
                        size="1rem"
                        style={{ textDecoration: 'underline' }}
                      >
                        View link
                      </Text>
                    )}
                  />
                ) : (
                  <Text color={page.primaryText} weight={600} size="1rem">—</Text>
                )}
              </Box>
              <Box direction="row" gap="0.25rem">
                <Text color={page.secondaryText} size="1rem" style={{ wordBreak: 'keep-all' }}>Monthly fee:</Text>
                <Text color={page.primaryText} weight={600} size="1rem">
                  $
                  {socialMonthly}
                  .00
                  {offerings.useCid && channels.length > 0 && (
                    ` (${channelCount} channel${channelCount > 1 ? 's' : ''})`
                  )}
                </Text>
              </Box>
            </Box>
          </ServiceSummaryContainer>
        )}
        {offerings.useWatermarking && (
          <ServiceSummaryContainer
            title="Watermarking"
            onRemove={() => handleRemoveFeature('useWatermarking')}
            borderColor={button.primaryBg}
            badge={(
              <Box
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                round="var(--app-radius)"
                background={button.primaryBg}
                margin={{ left: 'small' }}
              >
                <StyledUppercaseText size="0.85rem" weight={600} color="white">
                  Premium Feature
                </StyledUppercaseText>
              </Box>
            )}
          >
            <Box gap="0.5rem" width="100%">
              <Text color={page.primaryText} size="1rem" weight={500}>
                This is a premium feature. No additional cost will be added yet.
              </Text>
              <Text color={page.secondaryText} size="1rem">
                Our team will reach out separately to get you started
                with watermarking and discuss your needs.
              </Text>
            </Box>
          </ServiceSummaryContainer>
        )}
      </Box>
      {showChannelsModal && (
        <SimpleModal
          small={small}
          toggleModal={setShowChannelsModal}
          modalTitle={`YouTube Channels (${channels.length})`}
          highlightColor={button.primaryBg}
          hideLowerSection
          desktopMinWidth={small ? '96vw' : 'min(90vw, 32rem)'}
        >
          <Box
            width="100%"
            overflow={{ vertical: 'auto', horizontal: 'hidden' }}
            gap="0.5rem"
            style={{ maxHeight: '60vh' }}
          >
            {channels.map((ch) => (
              <Box
                key={ch}
                width="100%"
                minWidth={0}
                align="center"
                pad={{ vertical: '1rem', horizontal: '0.75rem' }}
                round="var(--app-radius)"
                background={button.selectBg}
                style={{ boxSizing: 'border-box' }}
              >
                <Text
                  size="0.85rem"
                  color={page.primaryText}
                  style={{
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    display: 'block',
                  }}
                >
                  {ch}
                </Text>
              </Box>
            ))}
          </Box>
        </SimpleModal>
      )}
      {showSocialModal && (
        <SimpleModal
          small={small}
          toggleModal={setShowSocialModal}
          modalTitle="Profile Link"
          highlightColor={button.primaryBg}
          hideLowerSection
          desktopMinWidth={small ? '96vw' : 'min(90vw, 32rem)'}
        >
          <Box
            round="var(--app-radius)"
            background={button.selectBg}
            pad={{ vertical: '0.5rem', horizontal: '0.75rem' }}
          >
            <Text size="0.9rem" color="var(--page-modal-text)" style={{ wordBreak: 'break-all' }}>
              {useSocialMainLink}
            </Text>
          </Box>
        </SimpleModal>
      )}
    </Box>
  );
};

PricingStep.propTypes = {
  small: PropTypes.bool.isRequired,
  offerings: PropTypes.shape({
    useCid: PropTypes.bool,
    useSocial: PropTypes.bool,
    useWatermarking: PropTypes.bool,
  }).isRequired,
  accountKind: PropTypes.oneOf(['creator', 'agency']).isRequired,
  channels: PropTypes.arrayOf(PropTypes.string),
  uploader: PropTypes.oneOf(['self', 'redflag']),
  cidPlan: PropTypes.oneOf(['A', 'B']),
  socialPlatforms: PropTypes.arrayOf(PropTypes.string),
  socialPlatformLabels: PropTypes.objectOf(PropTypes.string),
  socialMainLink: PropTypes.string,
  goToStep: PropTypes.func.isRequired,
  setOfferings: PropTypes.func.isRequired,
  earlyBirdEligibility: PropTypes.shape({
    eligible: PropTypes.bool,
    expiresAt: PropTypes.string,
    remainingSeconds: PropTypes.number,
    reason: PropTypes.string,
  }),
  pricingData: PropTypes.shape({
    version: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    effectiveAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    rules: PropTypes.shape({
      youtubeCid: PropTypes.shape({
        self: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
        }).isRequired,
        redflag: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      socialScanning: PropTypes.shape({
        perPlatformMonthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
      watermarking: PropTypes.shape({
        setupFeeAmount: PropTypes.number.isRequired,
        monthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
    display: PropTypes.shape({
      planLabels: PropTypes.shape({
        revenueShare: PropTypes.string.isRequired,
        hybrid: PropTypes.string.isRequired,
      }).isRequired,
      uploaderLabels: PropTypes.shape({
        self: PropTypes.string.isRequired,
        redflag: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }),
};

export default PricingStep;
